import React from "react";
import { Link } from "react-router-dom";
import {
  Zap,
  Snowflake,
  Camera,
  Cpu,
  Clock,
  Award,
  BadgeCheck,
  CheckCircle2,
  ArrowUpRight,
  Sparkles,
  Star,
  ShieldCheck,
  MapPin,
  Phone,
} from "lucide-react";
import { SITE, buildWaUrl, WA_MESSAGES, CTA_LABELS } from "../data/site";
import { AREAS_BATAM, AREAS_KEPRI, AREAS } from "../data/areas";
import { PRIMARY_SERVICES } from "../data/services";
import { BoltIcon, ElectricBackground, ArcDivider } from "../components/Decorations";
import Reveal from "../components/Reveal";
import Seo from "../components/Seo";
import Testimonials from "../components/Testimonials";
import Faq from "../components/Faq";
import Segments from "../components/Segments";

const SERVICE_ICONS = {
  "instalasi-perbaikan-listrik": Zap,
  "service-ac": Snowflake,
  "instalasi-cctv": Camera,
  "panel-industri": Cpu,
};

const SERVICE_WA = {
  "instalasi-perbaikan-listrik": WA_MESSAGES.installation,
  "service-ac": WA_MESSAGES.ac,
  "instalasi-cctv": WA_MESSAGES.cctv,
  "panel-industri": WA_MESSAGES.panel,
};

export default function Home() {
  const localBusinessLd = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: SITE.name,
    image: SITE.logo,
    "@id": "https://listrikbatam.com",
    url: "https://listrikbatam.com",
    telephone: "+62 811-7006-5656",
    email: SITE.email,
    address: {
      "@type": "PostalAddress",
      streetAddress: "Tiban Koperasi E 12 B",
      addressLocality: "Batam",
      addressRegion: "Kepulauan Riau",
      addressCountry: "ID",
    },
    areaServed: AREAS.map((a) => a.name),
    description: SITE.description,
    priceRange: "Rp",
    sameAs: [SITE.instagram],
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "4.9",
      reviewCount: "120",
    },
  };

  const faqLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "Berapa biaya jasa listrik di Batam?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Biaya tergantung jenis pekerjaan. Perbaikan ringan mulai Rp 150.000. Chat WhatsApp untuk estimasi gratis.",
        },
      },
      {
        "@type": "Question",
        name: "Apakah tersedia layanan 24 jam?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Ya, layanan panggilan darurat 24 jam tersedia untuk wilayah Batam.",
        },
      },
      {
        "@type": "Question",
        name: "Berapa lama teknisi sampai ke lokasi?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Untuk wilayah Batam umumnya 15–30 menit.",
        },
      },
    ],
  };

  return (
    <div data-testid="home-page">
      <Seo
        title="Jasa Listrik Batam — Teknisi 24 Jam, Respon Cepat | ListrikBatam.com"
        description="Jasa listrik terdekat di Batam: instalasi, perbaikan, service AC, CCTV, water heater, kulkas & mesin cuci. Panggilan cepat, teknisi 24 jam, bergaransi."
        canonical="https://listrikbatam.com/"
        jsonLd={localBusinessLd}
      />

      {/* ============ HERO ============ */}
      <section
        className="relative min-h-[92vh] flex items-end text-white overflow-hidden"
        data-testid="hero-section"
      >
        <ElectricBackground />

        <div className="relative max-w-7xl mx-auto px-6 md:px-10 lg:px-12 pt-28 pb-16 md:pb-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end w-full">
          <div className="lg:col-span-8">
            <Reveal>
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/20 text-white px-3 py-1.5 rounded-full text-xs font-semibold tracking-wide">
                <Sparkles className="w-3.5 h-3.5 text-[#FFB341]" />
                <span>Respon 24 Jam · Teknisi Bersertifikat · Bergaransi</span>
              </div>
            </Reveal>

            <Reveal as="h1" className="mt-5 font-display font-black leading-[0.95] tracking-tighter text-4xl sm:text-6xl lg:text-[84px] text-balance">
              Jasa Listrik
              <br />
              <span className="inline-flex items-center gap-3">
                Profesional
                <BoltIcon className="w-10 h-14 sm:w-14 sm:h-20 inline-block bolt-flicker" color="#FF6B00" />
              </span>
              <br />
              <span className="text-[#FF6B00]">di Batam.</span>
            </Reveal>

            <Reveal as="p" className="mt-7 max-w-2xl text-base md:text-lg text-white/80 leading-relaxed">
              Layanan teknis kelistrikan & peralatan rumah tangga dalam satu solusi
              terpadu. Listrik, AC, CCTV, water heater, kulkas & mesin cuci —
              respon <span className="text-white font-semibold">kurang dari 30 menit</span> untuk seluruh Batam & Kepulauan Riau.
            </Reveal>

            <Reveal className="mt-9 flex flex-col sm:flex-row gap-3 items-start" stagger>
              <a
                href={buildWaUrl(WA_MESSAGES.emergency)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-wa-cta"
                className="group inline-flex items-center justify-center gap-3 bg-[#FF6B00] text-white font-bold rounded-full pl-7 pr-5 py-4 text-base shadow-[0_12px_32px_rgba(255,107,0,0.45)] hover:-translate-y-1 hover:shadow-[0_18px_40px_rgba(255,107,0,0.55)] transition-all"
              >
                {CTA_LABELS.primary}
                <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-white text-[#FF6B00] transition-transform group-hover:rotate-45">
                  <ArrowUpRight className="w-4 h-4" />
                </span>
              </a>
              <Link
                to="/layanan"
                data-testid="hero-services-cta"
                className="inline-flex items-center justify-center gap-2 text-white font-semibold rounded-full px-7 py-4 text-base border border-white/25 hover:bg-white/10 transition-colors"
              >
                Lihat Semua Layanan
              </Link>
            </Reveal>

            <Reveal className="mt-4 text-xs text-white/60 flex items-center gap-2">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#FF6B00] animate-pulse" />
              Slot kunjungan hari ini terbatas — chat segera untuk amankan teknisi
            </Reveal>
          </div>

          {/* Right stats panel */}
          <Reveal className="lg:col-span-4 lg:justify-self-end w-full">
            <div className="relative rounded-3xl bg-white/10 backdrop-blur-lg border border-white/20 p-6 md:p-7 shadow-2xl">
              <div className="flex items-center gap-2 text-[#FFB341] text-xs uppercase tracking-[0.2em] font-bold">
                <Star className="w-3.5 h-3.5" />
                Trusted · Sejak {SITE.founded}
              </div>

              <div className="mt-5 grid grid-cols-2 gap-5">
                <div>
                  <div className="font-display text-4xl font-black">{SITE.technicianYears}+</div>
                  <div className="text-xs text-white/70 mt-1">Tahun pengalaman teknisi</div>
                </div>
                <div>
                  <div className="font-display text-4xl font-black text-[#FF6B00]">{SITE.clients}+</div>
                  <div className="text-xs text-white/70 mt-1">Pelanggan puas</div>
                </div>
                <div>
                  <div className="font-display text-4xl font-black">{SITE.areas}</div>
                  <div className="text-xs text-white/70 mt-1">Area layanan</div>
                </div>
                <div>
                  <div className="font-display text-4xl font-black text-[#FF6B00]">24/7</div>
                  <div className="text-xs text-white/70 mt-1">Respon darurat</div>
                </div>
              </div>

              <div className="mt-6 pt-5 border-t border-white/15 flex flex-wrap gap-2">
                <TrustBadge>Bergaransi</TrustBadge>
                <TrustBadge>Profesional</TrustBadge>
                <TrustBadge>Berpengalaman</TrustBadge>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ============ SERVICES PREVIEW (BENTO) ============ */}
      <section
        className="relative max-w-7xl mx-auto px-6 md:px-10 lg:px-12 py-24 md:py-32"
        data-testid="services-section"
      >
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end mb-14">
          <Reveal className="lg:col-span-7">
            <div className="text-[#FF6B00] font-semibold tracking-[0.2em] uppercase text-xs mb-3">
              / Layanan Utama
            </div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-black text-[#0F4C75] tracking-tight leading-[1.02] text-balance">
              Listrik, AC, CCTV — <span className="text-[#FF6B00]">semua dalam satu tim.</span>
            </h2>
          </Reveal>
          <Reveal className="lg:col-span-5 text-[#1B262C]/75 text-base md:text-lg">
            Layanan teknis kelistrikan dan peralatan rumah tangga dalam satu solusi
            terpadu — dikerjakan teknisi yang paham PUIL 2011 & SNI.
          </Reveal>
        </div>

        <Reveal
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-5 md:gap-6"
          stagger
          data-testid="services-grid"
        >
          {PRIMARY_SERVICES.map((svc, i) => {
            const Icon = SERVICE_ICONS[svc.slug] || Zap;
            const span =
              i === 0
                ? "lg:col-span-7 lg:row-span-2"
                : i === 1
                ? "lg:col-span-5"
                : i === 2
                ? "lg:col-span-5"
                : "lg:col-span-7";
            const isHero = i === 0;
            return (
              <Link
                key={svc.slug}
                to={`/layanan#${svc.slug}`}
                data-testid={`service-card-${svc.slug}`}
                className={`group relative overflow-hidden rounded-3xl border border-[#E5E7EB] p-7 md:p-9 transition-all duration-500 hover:-translate-y-2 hover:shadow-[0_20px_60px_-20px_rgba(15,76,117,0.35)] hover:border-[#3282B8] ${
                  isHero
                    ? "bg-gradient-to-br from-[#0F4C75] to-[#1B262C] text-white min-h-[320px] lg:min-h-[380px]"
                    : "bg-white text-[#1B262C]"
                } ${span}`}
              >
                {isHero && (
                  <div className="absolute inset-0 grid-bg opacity-20" />
                )}
                <div className="relative flex flex-col h-full">
                  <div
                    className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-6 transition-transform group-hover:rotate-[-6deg] ${
                      isHero
                        ? "bg-[#FF6B00] text-white"
                        : "bg-[#F8FAFC] text-[#0F4C75] group-hover:bg-[#FF6B00] group-hover:text-white"
                    }`}
                  >
                    <Icon className="w-6 h-6" />
                  </div>

                  <h3
                    className={`font-display font-bold text-2xl md:text-3xl tracking-tight mb-3 ${
                      isHero ? "text-white" : "text-[#0F4C75]"
                    }`}
                  >
                    {svc.title}
                  </h3>
                  <p
                    className={`text-sm md:text-base leading-relaxed max-w-md ${
                      isHero ? "text-white/80" : "text-[#1B262C]/75"
                    }`}
                  >
                    {svc.short}
                  </p>

                  {isHero && (
                    <ul className="mt-6 grid grid-cols-2 gap-2 text-sm text-white/85">
                      {svc.features.map((f) => (
                        <li key={f} className="flex items-center gap-2">
                          <CheckCircle2 className="w-4 h-4 text-[#FFB341]" />
                          {f}
                        </li>
                      ))}
                    </ul>
                  )}

                  <div className="mt-auto pt-6 flex items-center gap-2 text-sm font-semibold">
                    <span className={isHero ? "text-[#FFB341]" : "text-[#FF6B00]"}>
                      Selengkapnya
                    </span>
                    <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
                  </div>
                </div>
              </Link>
            );
          })}
        </Reveal>

        <Reveal className="mt-8 flex flex-wrap items-center gap-3">
          <span className="text-sm text-[#1B262C]/70">Juga tersedia:</span>
          <Link
            to="/layanan#service-mesin-cuci"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0F4C75] border border-[#E5E7EB] hover:border-[#FF6B00] rounded-full px-4 py-1.5 transition-colors"
          >
            Service Mesin Cuci
          </Link>
          <Link
            to="/layanan#service-kulkas"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0F4C75] border border-[#E5E7EB] hover:border-[#FF6B00] rounded-full px-4 py-1.5 transition-colors"
          >
            Service Kulkas
          </Link>
          <Link
            to="/layanan#instalasi-water-heater"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0F4C75] border border-[#E5E7EB] hover:border-[#FF6B00] rounded-full px-4 py-1.5 transition-colors"
          >
            Water Heater
          </Link>
          <Link
            to="/layanan#perbaikan-modul-pcb"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0F4C75] border border-[#E5E7EB] hover:border-[#FF6B00] rounded-full px-4 py-1.5 transition-colors"
          >
            Modul PCB
          </Link>
        </Reveal>
      </section>

      {/* ============ SEGMENTS ============ */}
      <section
        className="relative bg-[#F8FAFC] py-24 md:py-32 overflow-hidden"
        data-testid="segments-section"
      >
        <div className="absolute inset-0 grid-bg opacity-50" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end mb-12">
            <Reveal className="lg:col-span-7">
              <div className="text-[#FF6B00] font-semibold tracking-[0.2em] uppercase text-xs mb-3">
                / Untuk Berbagai Kebutuhan
              </div>
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-black text-[#0F4C75] tracking-tight leading-[1.02] text-balance">
                Dari rumah pribadi <br className="hidden md:inline" /> hingga <span className="text-[#FF6B00]">instansi pemerintah.</span>
              </h2>
            </Reveal>
            <Reveal className="lg:col-span-5 text-[#1B262C]/75 text-base md:text-lg">
              Layanan kami mencakup kebutuhan listrik untuk rumah tangga hingga
              proyek skala industri dan instansi pemerintah. Berpengalaman menangani
              berbagai kebutuhan mulai dari rumah pribadi hingga proyek skala besar.
            </Reveal>
          </div>

          <Segments />
        </div>
      </section>

      {/* ============ AREA LAYANAN ============ */}
      <section
        className="relative py-24 md:py-32 overflow-hidden"
        data-testid="area-section"
      >
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end mb-12">
            <Reveal className="lg:col-span-7">
              <div className="text-[#FF6B00] font-semibold tracking-[0.2em] uppercase text-xs mb-3">
                / Area Layanan Kami
              </div>
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-black text-[#0F4C75] tracking-tight leading-[1.02] text-balance">
                Seluruh Batam & <span className="text-[#FF6B00]">Kepulauan Riau.</span>
              </h2>
            </Reveal>
            <Reveal className="lg:col-span-5 text-[#1B262C]/75 text-base md:text-lg">
              Kami melayani jasa listrik di seluruh wilayah Batam seperti Batu
              Aji, Sekupang, Nagoya hingga Batam Center, serta wilayah
              Kepulauan Riau seperti Tanjung Pinang, Karimun, Natuna, dan
              Anambas.
            </Reveal>
          </div>

          <Reveal className="mb-4 flex items-center gap-3 text-sm font-semibold text-[#1B262C]">
            <span className="w-8 h-[2px] bg-[#0F4C75]" /> Batam
          </Reveal>
          <Reveal
            className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3 mb-12"
            stagger
          >
            {AREAS_BATAM.map((a) => (
              <Link
                key={a.slug}
                to={`/jasa-listrik/${a.slug}`}
                data-testid={`area-chip-${a.slug}`}
                className="group relative rounded-2xl bg-white border border-[#E5E7EB] px-4 py-5 text-center transition-all hover:-translate-y-1 hover:border-[#FF6B00] hover:shadow-[0_14px_30px_-10px_rgba(255,107,0,0.35)]"
              >
                <span className="block font-display font-bold text-[#0F4C75] text-sm md:text-base">
                  {a.name}
                </span>
                <span className="block text-[10px] uppercase tracking-wider text-[#1B262C]/50 mt-1">
                  Batam
                </span>
                <span className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <ArrowUpRight className="w-3.5 h-3.5 text-[#FF6B00]" />
                </span>
              </Link>
            ))}
          </Reveal>

          <Reveal className="mb-4 flex items-center gap-3 text-sm font-semibold text-[#1B262C]">
            <span className="w-8 h-[2px] bg-[#FF6B00]" /> Kepulauan Riau
          </Reveal>
          <Reveal
            className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3"
            stagger
          >
            {AREAS_KEPRI.map((a) => (
              <Link
                key={a.slug}
                to={`/jasa-listrik/${a.slug}`}
                data-testid={`area-chip-${a.slug}`}
                className="group rounded-2xl bg-gradient-to-br from-[#0F4C75] to-[#1B262C] text-white border border-transparent px-5 py-5 transition-all hover:-translate-y-1 hover:shadow-xl"
              >
                <span className="block font-display font-bold text-base md:text-lg">
                  {a.name}
                </span>
                <span className="block text-[10px] uppercase tracking-wider text-[#FFB341] mt-1">
                  Kepri
                </span>
              </Link>
            ))}
          </Reveal>
        </div>
      </section>

      {/* ============ LOCAL SEO (Jasa Listrik Terdekat) ============ */}
      <section
        className="bg-[#F8FAFC] py-20 md:py-24"
        data-testid="local-seo-section"
      >
        <div className="max-w-5xl mx-auto px-6 md:px-10 lg:px-12">
          <Reveal className="rounded-3xl bg-white border border-[#E5E7EB] p-8 md:p-12 relative overflow-hidden">
            <div
              className="absolute -top-20 -right-20 w-[320px] h-[320px] rounded-full blur-3xl opacity-40"
              style={{ background: "radial-gradient(closest-side, rgba(255,107,0,0.5), transparent 70%)" }}
            />
            <div className="relative flex items-center gap-3 text-[#FF6B00] font-semibold tracking-[0.2em] uppercase text-xs mb-4">
              <MapPin className="w-3.5 h-3.5" /> Jasa Listrik Terdekat
            </div>
            <h2 className="relative font-display text-3xl md:text-5xl font-black text-[#0F4C75] leading-[1.05] tracking-tight mb-6 text-balance">
              Teknisi listrik terdekat dari lokasi Anda.
            </h2>
            <p className="relative text-[#1B262C]/80 md:text-lg leading-relaxed">
              Kami menyediakan <strong>jasa listrik terdekat di Batam</strong> dengan
              layanan <strong>panggilan cepat</strong> dan <strong>teknisi siap 24 jam</strong>.
              Cukup chat WhatsApp dan sampaikan lokasi Anda — kami kirimkan teknisi
              yang paling dekat dengan estimasi tiba 15–30 menit untuk wilayah
              Batam. Untuk area Kepulauan Riau, kami jadwalkan proyek sesuai
              kebutuhan Anda.
            </p>
            <div className="relative mt-7 flex flex-wrap gap-3">
              <a
                href={buildWaUrl(WA_MESSAGES.emergency)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="local-seo-wa"
                className="inline-flex items-center gap-2 bg-[#FF6B00] text-white font-bold rounded-full pl-6 pr-4 py-3 text-sm shadow-[0_10px_24px_rgba(255,107,0,0.35)]"
              >
                {CTA_LABELS.fast}
                <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-white text-[#FF6B00]">
                  <ArrowUpRight className="w-3.5 h-3.5" />
                </span>
              </a>
              <a
                href={`tel:+${SITE.whatsappNumber}`}
                className="inline-flex items-center gap-2 border border-[#E5E7EB] text-[#0F4C75] font-semibold rounded-full px-6 py-3 text-sm hover:border-[#0F4C75] transition"
              >
                <Phone className="w-4 h-4" /> +62 811-7006-5656
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ============ WHY CHOOSE US ============ */}
      <section
        className="relative max-w-7xl mx-auto px-6 md:px-10 lg:px-12 py-24 md:py-32"
        data-testid="why-section"
      >
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <Reveal className="lg:col-span-5 lg:sticky lg:top-28">
            <div className="text-[#FF6B00] font-semibold tracking-[0.2em] uppercase text-xs mb-3">
              / Kenapa Pilih ListrikBatam?
            </div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-black text-[#0F4C75] tracking-tight leading-[1.02] text-balance">
              Bukan sekadar
              <br />
              <span className="text-[#FF6B00]">tukang listrik.</span>
            </h2>
            <p className="mt-6 text-[#1B262C]/75 text-base md:text-lg max-w-md">
              Kami bangun bisnis ini untuk satu hal: memberi warga Batam akses ke
              teknisi yang benar-benar bisa diandalkan — dari soal kecil sampai
              instalasi industri.
            </p>
            <a
              href={buildWaUrl(WA_MESSAGES.general)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="why-wa-cta"
              className="mt-8 inline-flex items-center gap-3 bg-[#0F4C75] text-white font-bold rounded-full pl-6 pr-4 py-3.5 text-sm hover:bg-[#0F4C75]/90 transition-colors"
            >
              {CTA_LABELS.clickHere}
              <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-[#FF6B00]">
                <ArrowUpRight className="w-4 h-4" />
              </span>
            </a>
          </Reveal>

          <Reveal
            className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-5"
            stagger
          >
            {[
              {
                icon: Award,
                title: "Teknisi Berpengalaman",
                body: "Bersertifikat kompetensi ketenagalistrikan dengan 15+ tahun jam terbang di proyek rumah, komersial, dan industri.",
              },
              {
                icon: Clock,
                title: "Respon Cepat Area Batam",
                body: "Teknisi tiba di lokasi 15–30 menit untuk seluruh area Batam. Tidak perlu menunggu seharian.",
              },
              {
                icon: BadgeCheck,
                title: "Harga Transparan",
                body: "Estimasi tertulis di awal. Tidak ada biaya kejutan. Anda setuju dulu, baru kami kerjakan.",
              },
              {
                icon: ShieldCheck,
                title: "Bergaransi",
                body: "Garansi pengerjaan hingga 6 bulan. Jika ada masalah yang sama, kami kembali tanpa biaya tambahan.",
              },
              {
                icon: MapPin,
                title: "Panggilan ke Lokasi",
                body: "Layanan on-call — kami datang ke rumah, ruko, atau pabrik Anda. Cukup chat WhatsApp dengan alamat.",
              },
              {
                icon: Sparkles,
                title: "Satu Solusi Terpadu",
                body: "Listrik, AC, CCTV, kulkas, mesin cuci, water heater — semua ditangani tim teknis yang sama.",
              },
            ].map(({ icon: Icon, title, body }) => (
              <div
                key={title}
                data-testid={`why-card-${title.toLowerCase().replace(/\s/g, "-")}`}
                className="group relative rounded-3xl border border-[#E5E7EB] bg-white p-7 hover:-translate-y-2 hover:border-[#3282B8] hover:shadow-[0_18px_40px_-18px_rgba(15,76,117,0.35)] transition-all"
              >
                <div className="w-12 h-12 rounded-xl bg-[#F8FAFC] text-[#0F4C75] group-hover:bg-[#FF6B00] group-hover:text-white flex items-center justify-center transition-colors mb-5">
                  <Icon className="w-5 h-5" />
                </div>
                <h3 className="font-display font-bold text-xl text-[#0F4C75] mb-2">
                  {title}
                </h3>
                <p className="text-sm text-[#1B262C]/75 leading-relaxed">
                  {body}
                </p>
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      {/* ============ TESTIMONIALS ============ */}
      <section
        className="bg-[#F8FAFC] py-24 md:py-28"
        data-testid="testimonials-section"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end mb-12">
            <Reveal className="lg:col-span-7">
              <div className="text-[#FF6B00] font-semibold tracking-[0.2em] uppercase text-xs mb-3">
                / Testimoni Pelanggan
              </div>
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-black text-[#0F4C75] tracking-tight leading-[1.02] text-balance">
                Kata mereka yang <span className="text-[#FF6B00]">sudah mencoba.</span>
              </h2>
            </Reveal>
            <Reveal className="lg:col-span-5 text-[#1B262C]/75 text-base md:text-lg">
              Pelanggan dari Batam Center, Sekupang, Nagoya, Tiban, dan sekitarnya
              mempercayakan kebutuhan teknis mereka pada kami.
            </Reveal>
          </div>
          <Testimonials />
        </div>
      </section>

      {/* ============ FAQ ============ */}
      <section
        className="max-w-5xl mx-auto px-6 md:px-10 lg:px-12 py-24 md:py-28"
        data-testid="faq-section"
      >
        <Reveal className="mb-10 text-center">
          <div className="text-[#FF6B00] font-semibold tracking-[0.2em] uppercase text-xs mb-3">
            / Pertanyaan Umum
          </div>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-black text-[#0F4C75] tracking-tight leading-[1.02] text-balance">
            Pertanyaan yang sering ditanyakan.
          </h2>
        </Reveal>
        <Faq />
        <Seo
          title="Jasa Listrik Batam — Teknisi 24 Jam, Respon Cepat | ListrikBatam.com"
          description="Jasa listrik terdekat di Batam: instalasi, perbaikan, service AC, CCTV, water heater, kulkas & mesin cuci. Panggilan cepat, teknisi 24 jam, bergaransi."
          canonical="https://listrikbatam.com/"
          jsonLd={faqLd}
        />
      </section>

      {/* ============ STRONG CTA ============ */}
      <section
        className="relative mx-6 md:mx-10 lg:mx-12 mb-24 md:mb-32 rounded-3xl overflow-hidden"
        data-testid="cta-section"
      >
        <div className="relative bg-gradient-to-br from-[#0F4C75] via-[#0F4C75] to-[#1B262C] px-8 md:px-16 py-16 md:py-24 text-white max-w-7xl mx-auto">
          <div className="absolute inset-0 grid-bg opacity-25" />
          <div className="absolute -top-20 -right-20 w-[380px] h-[380px] rounded-full blur-3xl opacity-60"
               style={{ background: "radial-gradient(closest-side, rgba(255,107,0,0.65), transparent 70%)" }}
          />
          <BoltIcon
            className="absolute top-8 right-10 w-14 h-20 float-slow bolt-flicker opacity-90"
            color="#FF6B00"
          />

          <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-8">
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-black leading-[1.02] tracking-tight text-balance">
                Siap benerin listrik
                <br /> <span className="text-[#FF6B00]">dalam 30 menit?</span>
              </h2>
              <p className="mt-5 text-white/80 max-w-2xl text-base md:text-lg">
                Chat kami di WhatsApp — ceritakan masalahnya, kami kirim
                teknisi & estimasi biaya hari ini juga.
              </p>
              <p className="mt-3 text-xs text-white/55">
                Slot terbatas setiap hari · Respon kurang dari 15 menit (jam operasional)
              </p>
            </div>
            <div className="lg:col-span-4 lg:justify-self-end">
              <a
                href={buildWaUrl(WA_MESSAGES.emergency)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="final-wa-cta"
                className="group inline-flex items-center gap-3 bg-[#FF6B00] text-white font-bold rounded-full pl-7 pr-5 py-4 text-base shadow-[0_14px_32px_rgba(255,107,0,0.45)] hover:-translate-y-1 transition-all"
              >
                {CTA_LABELS.whatsapp}
                <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-white text-[#FF6B00] transition-transform group-hover:rotate-45">
                  <ArrowUpRight className="w-4 h-4" />
                </span>
              </a>
            </div>
          </div>
          <ArcDivider className="absolute bottom-4 left-8 right-8 h-3 opacity-60" />
        </div>
      </section>
    </div>
  );
}

function TrustBadge({ children }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] font-bold bg-white/15 border border-white/20 text-white px-2.5 py-1 rounded-full">
      <BadgeCheck className="w-3 h-3 text-[#FFB341]" />
      {children}
    </span>
  );
}
